// src/App.tsx
import React, { Suspense, useEffect } from "react";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { MainLayout } from "@/layouts/MainLayout";
import { httpsCallable } from "firebase/functions";
import { functions } from "@/core/services/firebase";
import { LoadingProvider } from "@/context/LoadingContext";
import { ToastProvider } from "@/context/ToastContext";
import ErrorBoundary from "@/components/ErrorBoundary";
import LoadingSpinner from "@/components/LoadingSpinner";
import ScrollToHash from "@/components/ScrollToHash";
import config from "@/config/env";
import { trackPageView } from "@/utils/analytics";
import { captureError, addBreadcrumb } from "@/config/sentry";

const AnalyticsTracker: React.FC = () => {
  const location = useLocation();
  useEffect(() => {
    trackPageView(location.pathname + location.search);
  }, [location.pathname, location.search]);
  return null;
};

// Lazy load components
const BookingWizard = React.lazy(() => import("@/features/booking/BookingWizard"));
const BookingSuccess = React.lazy(() => import("@/pages/BookingSuccess"));
const BookingFailure = React.lazy(() => import("@/pages/BookingFailure"));
const LandingPage = React.lazy(() => import("@/pages/LandingPage"));
const RefundPolicy = React.lazy(() => import("@/pages/RefundPolicy"));
const BookingErrorDashboard = React.lazy(() => import("@/components/BookingErrorDashboard"));
const AdminAuth = React.lazy(() => import("@/components/AdminAuth"));
const AdminCompleteBooking = React.lazy(() => import("@/pages/AdminCompleteBooking"));

const LoadingFallback = () => (
  <div className="min-h-screen bg-gray-50 flex items-center justify-center">
    <LoadingSpinner size="large" />
  </div>
);

function App() {
  const handleEmailSend = async (bookingData: any) => {
    try {
      addBreadcrumb("booking", "Email send initiated", {
        orderId: bookingData.booking?.orderId || "unknown",
        brand: config.hotel
      });

      if (import.meta.env.DEV) {
        console.log("=== EMAIL SEND ATTEMPT START ===");
      }

      // Access customerInfo from the correct path
      const customerInfo = bookingData.booking.customerInfo;

      if (import.meta.env.DEV) {
        console.log("Verifying customer info:", {
          hasCustomerInfo: !!customerInfo,
          email: customerInfo?.email,
          name: customerInfo ? `${customerInfo.firstName} ${customerInfo.lastName}` : "Missing name"
        });
      }

      // Verify required data
      if (!customerInfo?.email) {
        console.error("Missing customer email in booking data:", bookingData);
        throw new Error("No recipient email found in booking data");
      }

      const sendEmail = httpsCallable(functions, "sendBookingEmail");

      // The data is already properly formatted in bookingData, just pass it through
      if (import.meta.env.DEV) {
        console.log("Sending email with formatted data:", bookingData);
      }
      const result = await sendEmail(bookingData);
      if (import.meta.env.DEV) {
        console.log("Email function response:", result);
        console.log("=== EMAIL SEND ATTEMPT END ===");
      }
      return true;
    } catch (error) {
      console.error("=== EMAIL SEND ERROR ===");
      console.error("Error details:", {
        error,
        message: error instanceof Error ? error.message : "Unknown error",
        stack: error instanceof Error ? error.stack : undefined,
        customerInfo: bookingData.booking.customerInfo,
        email: bookingData.booking.customerInfo?.email
      });

      // Report to Sentry with booking context
      captureError(error, {
        action: "email_send",
        orderId: bookingData.booking?.orderId || "unknown",
        customerEmail: bookingData.booking?.customerInfo?.email || "missing",
        brand: config.hotel
      });

      return false;
    }
  };

  return (
    <ErrorBoundary>
      <LoadingProvider>
        <ToastProvider>
          <BrowserRouter>
            <AnalyticsTracker />
            <ScrollToHash />
            <Routes>
              {/* Conditional rendering based on hotel config */}
              {config.hotel === "landing" ? (
                <>
                  {/* Landing page routes - no layout wrapper */}
                  <Route
                    index
                    element={
                      <Suspense fallback={<LoadingFallback />}>
                        <LandingPage />
                      </Suspense>
                    }
                  />
                  <Route
                    path="/refund-policy"
                    element={
                      <Suspense fallback={<LoadingFallback />}>
                        <RefundPolicy />
                      </Suspense>
                    }
                  />
                </>
              ) : (
                <>
                  {/* Main booking routes with layout */}
                  <Route element={<MainLayout />}>
                    <Route
                      index
                      element={
                        <Suspense fallback={<LoadingFallback />}>
                          <BookingWizard />
                        </Suspense>
                      }
                    />
                    <Route
                      path="booking"
                      element={
                        <Suspense fallback={<LoadingFallback />}>
                          <BookingWizard />
                        </Suspense>
                      }
                    />
                  </Route>
                </>
              )}

              {/* Payment response routes - outside MainLayout */}
              <Route
                path="/booking/success"
                element={
                  <Suspense fallback={<LoadingFallback />}>
                    <BookingSuccess onEmailSend={handleEmailSend} />
                  </Suspense>
                }
              />
              <Route
                path="/booking/failed"
                element={
                  <Suspense fallback={<LoadingFallback />}>
                    <BookingFailure />
                  </Suspense>
                }
              />

              {/* Admin routes - outside MainLayout */}
              <Route
                path="/admin/booking-errors"
                element={
                  <Suspense fallback={<LoadingFallback />}>
                    <AdminAuth>
                      <BookingErrorDashboard />
                    </AdminAuth>
                  </Suspense>
                }
              />
              <Route
                path="/admin/complete-booking"
                element={
                  <Suspense fallback={<LoadingFallback />}>
                    <AdminAuth>
                      <AdminCompleteBooking />
                    </AdminAuth>
                  </Suspense>
                }
              />

              {/* Catch-all redirect */}
              <Route path="*" element={<Navigate to="/" replace />} />
            </Routes>
          </BrowserRouter>
        </ToastProvider>
      </LoadingProvider>
    </ErrorBoundary>
  );
}

export default App;
